import React, { useEffect, useState } from 'react';
import { Download } from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import FileStatusBar, { FileStatus } from '../FileStatusBar/FileStatusBar';

interface UploadFilesProps {
  onFilesUploaded: (files: File | null) => void;
  uploadedFile: File | null;
}

const UploadFiles: React.FC<UploadFilesProps> = (props) => {
  const { onFilesUploaded, uploadedFile } = props;
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileStatus, setFileStatus] = useState<FileStatus>();
  const [fileErrorMessage, setFileErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const timeoutRef = React.useRef<any>(null);
  const [firstTimeRendering, setFirstTimeRendering] = useState(true);
  const [fileInput, setFileInput] = useState<File | null>(null);

  useEffect(() => {
    setFirstTimeRendering(false);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (uploadedFile && firstTimeRendering) {
      setFileInput(uploadedFile);
      setFileStatus('complete');
    }
  }, [uploadedFile]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file =
      event.dataTransfer.files.length > 0 ? event.dataTransfer.files[0] : null;
    handleFileUpload(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    handleFileUpload(file);
  };

  const handleFileUpload = (file: File | null) => {
    setFileInput(file);
    processFiles(file);
    onFilesUploaded(file);
  };

  const processFiles = (file: File | null) => {
    if (
      file?.type !== 'text/csv' &&
      file?.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFileStatus('error');
      setFileErrorMessage(
        'Invalid file type. Please upload a CSV or Excel file.',
      );
      return;
    }

    const reader = new FileReader();

    reader.onprogress = (event) => {
      setFileStatus('in-progress');
      if (event.lengthComputable) {
        const percentLoaded = Math.round((event.loaded / event.total) * 100);
        setProgress(percentLoaded);
      }
    };

    reader.onload = () => {
      setFileUploadCompleted();
    };

    reader.onerror = () => {
      setFileStatus('error');
    };

    setProgress(0);
    reader.readAsText(file);
  };

  const setFileUploadCompleted = () => {
    setProgress(100);
    timeoutRef.current = setTimeout(() => {
      setFileStatus('complete');
    }, 300);
  };

  const handleClearFile = () => {
    setProgress(0);
    setFileStatus(undefined);
    onFilesUploaded(null);
    setFileInput(null);
  };

  if (fileInput) {
    return (
      <div>
        <FileStatusBar
          fileName={fileInput.name}
          status={fileStatus}
          progress={progress}
          onDelete={handleClearFile}
          onRetry={handleClearFile}
          errorMessage={fileErrorMessage}
        />
      </div>
    );
  }

  const title = dragging ? 'Drop here' : 'Drag and drop here';

  return (
    <UploadFilesContainer
      isDragging={dragging ? true : false}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <UploadFilesTitle>
        <Download height={18} width={18} />
        <span>{title}</span>
      </UploadFilesTitle>
      {!dragging ? (
        <>
          <input
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          />
          <BrowseButton htmlFor="file-upload">Browse Files</BrowseButton>
          <UploadCsv>Supported Files: CSV</UploadCsv>
        </>
      ) : null}
    </UploadFilesContainer>
  );
};

export default UploadFiles;

const UploadFilesContainer = styled.div<{ isDragging: boolean }>`
  border: ${(props) =>
    props.isDragging ? `2px solid #21bcb6` : `1px dashed #b6bbc1`};
  padding: 75px 20px;
  text-align: center;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 250px;
  background-color: ${(props) =>
    props.isDragging
      ? props.theme.colors.interactive[10]
      : props.theme.colors.background};
  color: ${(props) =>
    props.isDragging
      ? props.theme.colors.interactive[100]
      : props.theme.colors.primary[100]};
`;

const UploadCsv = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${(props) => props.theme.colors.primary[60]};
`;

const UploadFilesTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;

  span {
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
  }
`;

const BrowseButton = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background-color: ${({ theme }: any) => theme.colors.primary[0]};
  border: 1px solid ${({ theme }: any) => theme.colors.primary[40]};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 12px;
  margin: 16px 0;
  cursor: pointer;
`;
